/**
 * One person in the room, opened from their tile.
 *
 * *** WHAT IS DELIBERATELY NOT HERE ***
 * No Message, no Nudge, no Add friend. A private message between two
 * students raises the plan-gate question the library module exists to stay
 * clear of, and peer nudges were parked. Buttons that do nothing are worse
 * than buttons that are absent.
 *
 * *** FIRST NAME, FIGURES AND BADGES ***
 * That is everything the server sends. No email, no phone: students have
 * not consented to be listed to each other in full, and the platform has
 * under-18 users.
 *
 * *** FADED BADGES ARE THE POINT ***
 * Every badge is rendered, earned or not. A cabinet showing only what
 * somebody has gives a viewer nothing to aim at — the faded ones, with
 * what earns them, are what make it a goal rather than a trophy.
 */

import { useEffect, useRef } from 'react'
import { usePersonCard } from '@/lib/queries'
import type { LibraryOccupant } from '@/types/api'
import { hoursMinutes, initials } from './format'
import styles from './OccupantCard.module.css'

/** A glyph per badge. Unknown keys fall back rather than rendering blank. */
const BADGE_ICONS: Record<string, string> = {
  FIRST_HOUR: '⏱',
  DEEP_FOCUS: '◎',
  MARATHON: '⛰',
  NIGHT_OWL: '☾',
  EARLY_BIRD: '☀',
  WEEK_STRONG: '▦',
  ON_CAMERA: '▣',
}

export function OccupantCard({
  person,
  libraryId,
  onClose,
}: {
  person: LibraryOccupant
  libraryId: string
  onClose: () => void
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null)
  const { data } = usePersonCard(libraryId, person.user_id)

  /*
    Escape closes it, and focus moves to the close button on open. A modal
    that traps neither is one a keyboard user cannot leave.
  */
  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const joined = new Date(person.joined_at)
  const joinedLabel = Number.isNaN(joined.getTime())
    ? null
    : joined.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  const badges = data?.badges ?? []
  const earnedCount = badges.filter((b) => b.earned).length
  const isMentor = person.role === 'MENTOR'

  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-label={`About ${person.name}`}
        /* The backdrop closes; a click inside must not bubble up to it. */
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {/* --------------------------------------------- left column */}
        <div className={styles.portrait}>
          {/*
            The camera slot, at the size a SELECTED tile will stream at.
            Square rather than the grid's 4:3 — a portrait of one person
            reads better square, and this is the only place a higher
            resolution is requested. The grid stays low-resolution, which
            is what keeps the bandwidth bill survivable.
          */}
          <div className={styles.slot}>
            <span className={styles.avatar}>{initials(person.name)}</span>
          </div>
          {isMentor && <span className={styles.mentorTag}>Mentor</span>}
        </div>

        {/* -------------------------------------------- right column */}
        <div className={styles.facts}>
          <h3 className={styles.name}>{person.name}</h3>

          {data?.title && <p className={styles.role}>{data.title}</p>}

          <div className={styles.figures}>
            <div className={styles.fact}>
              <span className={styles.factLabel}>
                {/* "This session" reads as study time; a mentor is on duty. */}
                {isMentor ? 'In the room' : 'This session'}
              </span>
              <span className={styles.factValue}>
                {hoursMinutes(person.active_seconds)}
              </span>
            </div>

            {/*
              Figures that need the server. Absent until it answers rather
              than flashing a zero, which would read as a real total and
              then change under the viewer.
            */}
            {data && isMentor && (
              <div className={styles.fact}>
                <span className={styles.factLabel}>On duty, all time</span>
                <span className={styles.factValue}>
                  {hoursMinutes(data.supervision_seconds)}
                </span>
              </div>
            )}

            {data && !isMentor && (
              <>
                <div className={styles.fact}>
                  <span className={styles.factLabel}>All time</span>
                  <span className={styles.factValue}>
                    {hoursMinutes(data.total_seconds)}
                  </span>
                </div>
                <div className={styles.fact}>
                  <span className={styles.factLabel}>Streak</span>
                  <span className={styles.factValue}>
                    {data.day_streak}
                    <span className={styles.unit}>
                      {data.day_streak === 1 ? ' day' : ' days'}
                    </span>
                  </span>
                </div>
              </>
            )}

            {joinedLabel && (
              <div className={styles.fact}>
                <span className={styles.factLabel}>Arrived</span>
                <span className={styles.factValue}>{joinedLabel}</span>
              </div>
            )}
          </div>
        </div>

        {/*
          *** WHAT A MENTOR TEACHES, IN PLACE OF BADGES ***
          Badges measure study, which a mentor is not doing. What a student
          wants before asking a question is whether this is the right
          person to ask.
        */}
        {isMentor && data && (data.expertise.length > 0 || data.bio) && (
          <section className={styles.badges} aria-label="About this mentor">
            {data.expertise.length > 0 && (
              <>
                <h4 className={styles.badgeTitle}>Teaches</h4>
                <ul className={styles.tags}>
                  {data.expertise.map((tag) => (
                    <li key={tag} className={styles.tag}>
                      {tag}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {data.bio && <p className={styles.bio}>{data.bio}</p>}
          </section>
        )}

        {!isMentor && badges.length > 0 && (
          <section className={styles.badges} aria-label="Badges">
            <h4 className={styles.badgeTitle}>
              Badges
              <span className={styles.badgeCount}>
                {earnedCount} of {badges.length}
              </span>
            </h4>
            <ul className={styles.badgeList}>
              {badges.map((badge) => (
                <li
                  key={badge.key}
                  className={`${styles.badge} ${
                    badge.earned ? styles.badgeEarned : ''
                  }`}
                  /* The rule, on hover and to a screen reader. A faded icon
                     with no explanation is a puzzle. */
                  title={badge.how}
                >
                  <span className={styles.badgeIcon} aria-hidden="true">
                    {BADGE_ICONS[badge.key] ?? '★'}
                  </span>
                  <span className={styles.badgeLabel}>{badge.label}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className={styles.note}>
          {isMentor
            ? 'Ask your question in the room chat — the mentor answers there.'
            : 'Say hello in the room chat.'}
        </p>
      </div>
    </div>
  )
}
